/**
 * Integrytas Group - Common Security Module (CSM)
 * SortableHeader - drop-in <th> wrapper that drives a useSort() hook.
 *
 * Pairs with src/lib/use_sort.ts. The component is generic on the sort-key
 * union so callers get TS errors if they pass a key the table doesn't support.
 *
 * Usage:
 *   <SortableHeader<UserSortKey>
 *     sortKey="email" label="Email"
 *     currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />
 */

import type React from 'react';
import type { SortDir } from '../../lib/use_sort';

export interface SortableHeaderProps<K extends string> {
  sortKey:   K;
  label:     string;
  currentKey: K | null;
  currentDir: SortDir;
  onSort:    (key: K) => void;
  align?:    'left' | 'right';
  className?: string;
  style?:     React.CSSProperties;
}

export function SortableHeader<K extends string>({
  sortKey, label, currentKey, currentDir, onSort, align = 'left', className, style,
}: SortableHeaderProps<K>) {
  const isActive = currentKey === sortKey;
  const justify  = align === 'right' ? 'justify-end' : 'justify-start';
  const textAlign = align === 'right' ? 'text-right' : 'text-left';

  return (
    <th
      onClick={() => onSort(sortKey)}
      style={style}
      className={
        'cursor-pointer select-none border-b border-csm-line bg-csm-surface2 px-3 py-2.5 ' +
        'text-csm-grid-h uppercase text-csm-muted ' +
        textAlign + ' hover:bg-csm-hair ' + (className ?? '')
      }
      aria-sort={isActive ? (currentDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span className={'inline-flex items-center gap-1 ' + justify}>
        <span>{label}</span>
        <span aria-hidden className={'text-csm-2xs ' + (isActive ? 'text-primary' : 'text-csm-ink4')}>
          {isActive ? (currentDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </span>
    </th>
  );
}
